import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPlayers, getDistricts, Player, District } from '../api/players.api';
import PlayerProfileModal from '../components/PlayerProfileModal';

const roleOptions: string[] = ['Batsman', 'Bowler', 'All-Rounder', 'Wicket-Keeper'];

const matchAgeRange = (age: number, range: string) => {
  switch (range) {
    case 'under-18':
      return age < 18;
    case '18-25':
      return age >= 18 && age <= 25;
    case '26-30':
      return age >= 26 && age <= 30;
    case '30-plus':
      return age >= 30;
    default:
      return true;
  }
};

const ageOptions = [
  { label: 'Under 18', value: 'under-18' },
  { label: '18-25', value: '18-25' },
  { label: '26-30', value: '26-30' },
  { label: '30+', value: '30-plus' },
];

const PlayersPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedAgeRanges, setSelectedAgeRanges] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [ageSearch, setAgeSearch] = useState('');
  const [districtSearch, setDistrictSearch] = useState('');
  const [brokenThumbnails, setBrokenThumbnails] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchPlayers();
    fetchDistricts();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await getPlayers();
      const playersData = response.data || [];
      console.log('PlayersPage - Fetched players:', playersData.length);
      console.log('PlayersPage - First player:', playersData[0]);
      console.log('PlayersPage - First player playerId:', playersData[0]?.playerId);
      
      // Log players with playerId
      const playersWithId = playersData.filter(p => p.playerId);
      console.log('PlayersPage - Players with playerId:', playersWithId.length, 'out of', playersData.length);
      if (playersWithId.length > 0) {
        console.log('PlayersPage - Sample player with ID:', playersWithId[0]);
      }
      
      setPlayers(playersData);
    } catch (err) {
      console.error('PlayersPage - Error fetching players:', err);
      setPlayers([]);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await getDistricts();
      setDistricts(response.data || []);
    } catch (err) {
      setDistricts([]);
    }
  };

  const filteredPlayers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return players.filter((player) => {
      const matchesSearch =
        !query ||
        player.name.toLowerCase().includes(query) ||
        (player.district && player.district.toLowerCase().includes(query)) ||
        (player.playerId && player.playerId.toLowerCase().includes(query));
      const matchesRole =
        selectedRoles.length === 0 || selectedRoles.includes(player.role);
      const matchesDistrict =
        selectedDistricts.length === 0 || (player.districtCode && selectedDistricts.includes(player.districtCode));
      const matchesAge =
        selectedAgeRanges.length === 0 ||
        selectedAgeRanges.some((range) => {
          if (!player.age) return false;
          return matchAgeRange(player.age, range);
        });

      return matchesSearch && matchesRole && matchesDistrict && matchesAge;
    });
  }, [players, searchQuery, selectedRoles, selectedDistricts, selectedAgeRanges]);

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
    setShowModal(true);
  };

  const handleThumbnailError = (playerId: string) => {
    setBrokenThumbnails((prev) => ({ ...prev, [playerId]: true }));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPlayer(null);
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((item) => item !== role) : [...prev, role]
    );
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedRoles([]);
    setSelectedAgeRanges([]);
    setSelectedDistricts([]);
    setAgeSearch('');
    setDistrictSearch('');
  };

  const isClearDisabled =
    searchQuery.length === 0 &&
    selectedRoles.length === 0 &&
    selectedAgeRanges.length === 0 &&
    selectedDistricts.length === 0;

  const filteredAgeOptions = ageOptions.filter((option) =>
    option.label.toLowerCase().includes(ageSearch.trim().toLowerCase())
  );
  const filteredDistricts = districts.filter((district) =>
    district.name.toLowerCase().includes(districtSearch.trim().toLowerCase())
  );

  const toggleSelection = (value: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const handleSelectAll = (options: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(options);
  };

  const handleClearSelection = (setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter([]);
  };

  const handleRemoveFilter = (type: 'role' | 'age' | 'district', value: string) => {
    if (type === 'role') {
      setSelectedRoles(prev => prev.filter(item => item !== value));
    } else if (type === 'age') {
      setSelectedAgeRanges(prev => prev.filter(item => item !== value));
    } else if (type === 'district') {
      setSelectedDistricts(prev => prev.filter(item => item !== value));
    }
  };

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Home
        </Link>
      </div>
      <div className="mb-4">
        <h1 className="h3 section-title">Players</h1>
        <p className="text-muted-soft">Discover standout talent across the league.</p>
      </div>

      <div className="mb-4">
        <input
          type="search"
          className="form-control"
          placeholder="Search by player name, ID (e.g., SZ-2501), or district"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          aria-label="Search players by name, ID, or district"
          autoFocus
        />
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-8">
          {filteredPlayers.length === 0 ? (
            <div className="alert alert-secondary">No players found</div>
          ) : (
            <div className="row g-4">
              {filteredPlayers.map((player) => (
                <div className="col-12 col-md-6 col-lg-4" key={player.id}>
                  <div
                    className="card card-surface h-100"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handlePlayerClick(player)}
                  >
                    {player.thumbnail && !brokenThumbnails[player.id] ? (
                      <div className="player-card-image-container">
                      <img
                        src={player.thumbnail}
                        alt={player.name}
                          className="card-img-top player-card-image"
                        onError={() => handleThumbnailError(player.id)}
                      />
                      </div>
                    ) : (
                      <div
                        className="card-img-top bg-dark d-flex align-items-center justify-content-center text-white"
                        style={{ height: '200px', backgroundColor: '#0a0a0a' }}
                      >
                        <span style={{ fontSize: '3rem' }}>{player.name.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="card-title fw-bold mb-0">{player.name}</h5>
                        {player.playerId ? (
                          <span className="badge bg-success text-white fw-bold" style={{ fontSize: '0.75rem' }}>
                            {player.playerId}
                          </span>
                        ) : (
                          <span className="badge bg-secondary text-white fw-bold" style={{ fontSize: '0.65rem' }} title="Player ID not available from backend">
                            No ID
                          </span>
                        )}
                      </div>
                      <p className="text-muted-soft mb-2">{player.role}</p>
                      <div className="small">
                        <div className="mb-2">
                          <span className="text-muted-soft">Player ID:</span>{' '}
                          {player.playerId ? (
                            <span className="fw-bold text-success">{player.playerId}</span>
                          ) : (
                            <span className="fw-bold text-warning" title="Backend not returning player_id">Not Available</span>
                          )}
                        </div>
                        <div>
                          <span className="text-muted-soft">Age:</span> {player.age}
                        </div>
                        <div>
                          <span className="text-muted-soft">District:</span> {player.district}
                        </div>
                        {player.currentTeam && (
                          <div>
                            <span className="text-muted-soft">Team:</span> {player.currentTeam}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 small text-danger fw-semibold">
                        FOR HIRE THIS PLAYER<br />
                        CALL TO- 01788-083425
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-12 col-lg-4 d-flex justify-content-center">
          <div className="filter-sidebar w-100">
            <div className="card card-surface filter-card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="mb-0 fw-bold">Filters</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleClearFilters}
                    disabled={isClearDisabled}
                  >
                    Clear All
                  </button>
                </div>

                {(selectedRoles.length > 0 || selectedAgeRanges.length > 0 || selectedDistricts.length > 0) && (
                  <div className="mb-4">
                    <p className="small fw-semibold mb-2 text-muted-soft">Active Filters:</p>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedRoles.map((role) => (
                        <span key={role} className="active-filter-indicator">
                          Role: {role}
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => handleRemoveFilter('role', role)}
                            aria-label={`Remove ${role} filter`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {selectedAgeRanges.map((age) => {
                        const ageLabel = ageOptions.find(opt => opt.value === age)?.label || age;
                        return (
                          <span key={age} className="active-filter-indicator">
                            Age: {ageLabel}
                            <button
                              type="button"
                              className="remove-btn"
                              onClick={() => handleRemoveFilter('age', age)}
                              aria-label={`Remove ${ageLabel} filter`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      {selectedDistricts.map((district) => {
                        const districtName = districts.find(d => d.code === district)?.name || district;
                        return (
                          <span key={district} className="active-filter-indicator">
                            {districtName}
                            <button
                              type="button"
                              className="remove-btn"
                              onClick={() => handleRemoveFilter('district', district)}
                              aria-label={`Remove ${districtName} filter`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className="form-label fw-semibold mb-3 d-block">Role</label>
                  {roleOptions.map((role) => (
                    <div className="form-check" key={role}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`role-${role}`}
                        checked={selectedRoles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                      />
                      <label className="form-check-label" htmlFor={`role-${role}`}>
                        {role}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2 d-block">Age</label>
                  <div className="dropdown">
                    <button
                      className={`btn btn-outline-secondary w-100 d-flex align-items-center justify-content-between ${selectedAgeRanges.length > 0 ? 'border-danger' : ''}`}
                      type="button"
                      id="ageDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span>
                        {selectedAgeRanges.length > 0
                          ? `${selectedAgeRanges.length} selected`
                          : 'Select age ranges'}
                      </span>
                      {selectedAgeRanges.length > 0 && (
                        <span className="badge bg-danger ms-2">{selectedAgeRanges.length}</span>
                      )}
                      <i className="bi bi-chevron-down ms-auto"></i>
                    </button>
                    <ul className="dropdown-menu w-100 p-3" aria-labelledby="ageDropdown" onClick={(e) => e.stopPropagation()}>
                      <li>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          placeholder="Search..."
                          value={ageSearch}
                          onChange={(e) => setAgeSearch(e.target.value)}
                        />
                      </li>
                      <li><hr className="dropdown-divider" /></li>
                      {filteredAgeOptions.map((option) => (
                        <li key={option.value}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`age-${option.value}`}
                              checked={selectedAgeRanges.includes(option.value)}
                              onChange={() => toggleSelection(option.value, setSelectedAgeRanges)}
                            />
                            <label className="form-check-label" htmlFor={`age-${option.value}`}>
                              {option.label}
                            </label>
                          </div>
                        </li>
                      ))}
                      {filteredAgeOptions.length === 0 && (
                        <li><span className="dropdown-item-text text-muted">No options found</span></li>
                      )}
                      <li><hr className="dropdown-divider" /></li>
                      <li className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary flex-fill"
                          onClick={() => handleSelectAll(ageOptions.map(opt => opt.value), setSelectedAgeRanges)}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary flex-fill"
                          onClick={() => handleClearSelection(setSelectedAgeRanges)}
                        >
                          Clear
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2 d-block">District</label>
                  <div className="dropdown">
                    <button
                      className={`btn btn-outline-secondary w-100 d-flex align-items-center justify-content-between ${selectedDistricts.length > 0 ? 'border-danger' : ''}`}
                      type="button"
                      id="districtDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span>
                        {selectedDistricts.length > 0
                          ? `${selectedDistricts.length} selected`
                          : 'Select districts'}
                      </span>
                      {selectedDistricts.length > 0 && (
                        <span className="badge bg-danger ms-2">{selectedDistricts.length}</span>
                      )}
                      <i className="bi bi-chevron-down ms-auto"></i>
                    </button>
                    <ul className="dropdown-menu w-100 p-3" aria-labelledby="districtDropdown" onClick={(e) => e.stopPropagation()}>
                      <li>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-2"
                          placeholder="Search..."
                          value={districtSearch}
                          onChange={(e) => setDistrictSearch(e.target.value)}
                        />
                      </li>
                      <li><hr className="dropdown-divider" /></li>
                      {filteredDistricts.map((district) => (
                        <li key={district.code}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`district-${district.code}`}
                              checked={selectedDistricts.includes(district.code)}
                              onChange={() => toggleSelection(district.code, setSelectedDistricts)}
                            />
                            <label className="form-check-label" htmlFor={`district-${district.code}`}>
                              {district.name}
                            </label>
                          </div>
                        </li>
                      ))}
                      {filteredDistricts.length === 0 && (
                        <li><span className="dropdown-item-text text-muted">No options found</span></li>
                      )}
                      <li><hr className="dropdown-divider" /></li>
                      <li className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary flex-fill"
                          onClick={() => handleSelectAll(districts.map(d => d.code), setSelectedDistricts)}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary flex-fill"
                          onClick={() => handleClearSelection(setSelectedDistricts)}
                        >
                          Clear
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="small text-muted-soft pt-3 border-top border-secondary">
                  <strong className="text-white">{filteredPlayers.length}</strong> of <strong className="text-white">{players.length}</strong> players
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Player Profile Modal */}
      <PlayerProfileModal player={selectedPlayer} show={showModal} onHide={handleCloseModal} />
    </div>
  );
};

export default PlayersPage;
